/**
 * Reconciliation Queue View - Shows unmatched/ambiguous bank transactions
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import {
  CheckCircle,
  Refresh,
  UploadFile,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBankStatements,
  getBankTransaction,
  getBankTransactions,
  searchBankMatchCandidates,
  importBankStatementPdf,
  matchBankTransaction,
} from "../services/bankTransactionService.js";
import { format } from "date-fns";
import CustomerSearchAutocomplete from "@/shared/components/CustomerSearchAutocomplete.jsx";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "@/shared/components/pageShell";

const HiddenFileInput = styled.input`
  display: none;
`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount || 0);
};

const BANK_NAME_STOP = new Set([
  "PAYSHAP",
  "CREDIT",
  "DEBIT",
  "MAGTAPE",
  "RTC",
  "FNB",
  "APP",
  "TRANSFER",
  "PAYMENT",
  "FROM",
  "BANK",
  "CAPITEC",
  "ABSA",
  "INVESTEC",
]);

const nameHintFromReference = (referenceText) => {
  const tokens = String(referenceText || "")
    .split(/[\s/_.,-]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 4 && !/\d/.test(token))
    .filter((token) => !BANK_NAME_STOP.has(token.toUpperCase()))
    .filter((token) => !/^(BUB|INV|QT|REF)/i.test(token));
  return tokens
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");
};

const isCandidateSelected = (candidate, matchData) => {
  if (candidate.invoiceId && matchData.invoiceId === candidate.invoiceId) return true;
  if (!candidate.invoiceId && candidate.quoteId && matchData.quoteId === candidate.quoteId) {
    return true;
  }
  if (
    !candidate.invoiceId &&
    !candidate.quoteId &&
    candidate.jobId &&
    matchData.jobId === candidate.jobId
  ) {
    return true;
  }
  return false;
};

const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch {
    return "Invalid date";
  }
};

const ReconciliationQueueView = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("UNMATCHED");
  const [importMessage, setImportMessage] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [showManualIds, setShowManualIds] = useState(false);
  const [matchData, setMatchData] = useState({
    jobId: "",
    invoiceId: "",
    quoteId: "",
    type: "BALANCE",
  });

  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bankTransactions", statusFilter],
    queryFn: () =>
      getBankTransactions({
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: 100,
      }),
  });

  const statementsQuery = useQuery({
    queryKey: ["bankStatements"],
    queryFn: getBankStatements,
  });

  const importMutation = useMutation({
    mutationFn: (file) => importBankStatementPdf(file),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bankTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["bankStatements"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (!result.success) {
        setImportMessage({ severity: "error", text: result.error || "Import failed" });
        return;
      }
      if (result.skipped) {
        setImportMessage({
          severity: "info",
          text: `Already imported ${result.data?.sourceFilename || "this statement"}.`,
        });
        return;
      }
      setImportMessage({
        severity: "success",
        text: `Imported ${result.imported} lines from ${result.data?.sourceFilename || "statement"}. Closing cash ${formatCurrency(result.data?.closingBalance)}.`,
      });
    },
  });

  const matchMutation = useMutation({
    mutationFn: ({ id, data }) => matchBankTransaction(id, data),
    onSuccess: (result) => {
      if (!result.success) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["bankTransactions"] });
      setMatchDialogOpen(false);
      setSelectedTransaction(null);
      setMatchData({
        jobId: "",
        invoiceId: "",
        quoteId: "",
        type: "BALANCE",
      });
    },
  });

  const transactions = data?.success ? data.data : [];
  const errorMessage = error?.message || (data && !data.success ? data.error : null);

  const statements = statementsQuery.data?.success ? statementsQuery.data.data : [];

  const handleStatementFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportMessage(null);
    importMutation.mutate(file);
  };

  const applyCandidate = (candidate) => {
    setMatchData({
      jobId: candidate.jobId || "",
      invoiceId: candidate.invoiceId || "",
      quoteId: candidate.quoteId || "",
      type: candidate.paymentType || "BALANCE",
    });
  };

  const handleMatchClick = async (transaction) => {
    const hint = nameHintFromReference(transaction.referenceText);
    setSelectedTransaction(transaction);
    setCandidates([]);
    setCustomerSearch(hint);
    setPickedCustomer(null);
    setSearchError(null);
    setShowManualIds(false);
    setMatchData({
      jobId: "",
      invoiceId: "",
      quoteId: "",
      type: "BALANCE",
    });
    setMatchDialogOpen(true);

    if (hint) return;

    setCandidatesLoading(true);
    const detail = await getBankTransaction(transaction.id);
    const nextCandidates = detail.success ? detail.data?.candidates || [] : [];
    setCandidates(nextCandidates);
    if (nextCandidates[0]) applyCandidate(nextCandidates[0]);
    setCandidatesLoading(false);
  };

  useEffect(() => {
    if (!matchDialogOpen || !selectedTransaction) return undefined;
    const query = customerSearch.trim();
    if (query.length < 2) return undefined;
    const handle = setTimeout(async () => {
      setCandidatesLoading(true);
      setSearchError(null);
      const searched = await searchBankMatchCandidates({
        query,
        amount: selectedTransaction.amount,
        occurredAt: selectedTransaction.occurredAt,
      });
      const next = searched.success ? searched.data : [];
      setCandidates(next);
      setSearchError(searched.success ? null : searched.error || "Customer search failed");
      if (next[0]) applyCandidate(next[0]);
      setCandidatesLoading(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [customerSearch, matchDialogOpen, selectedTransaction]);

  const handleMatchSubmit = () => {
    if (!selectedTransaction) return;

    const submitData = {
      type: matchData.type,
    };
    if (matchData.invoiceId) submitData.invoiceId = matchData.invoiceId;
    if (matchData.quoteId) submitData.quoteId = matchData.quoteId;
    if (matchData.jobId) submitData.jobId = matchData.jobId;

    matchMutation.mutate({
      id: selectedTransaction.id,
      data: submitData,
    });
  };

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <PageHeader
        title="Payment Reconciliation"
        back={{
          label: "Finance",
          shortLabel: "Finance",
          onClick: () => navigate("/finance"),
        }}
        actions={
          <>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleStatementFile}
            />
            <Button
              variant="contained"
              startIcon={importMutation.isPending ? <CircularProgress size={16} /> : <UploadFile />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Upload FNB PDF
            </Button>
            <IconButton onClick={() => refetch()} aria-label="Refresh">
              <Refresh />
            </IconButton>
          </>
        }
      />

      <PageFilters>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{
            minWidth: { xs: "100%", sm: 150 },
            flex: { xs: 1, sm: "none" },
            bgcolor: "background.paper",
          }}
          size="small"
        >
          <MenuItem value="UNMATCHED">Unmatched</MenuItem>
          <MenuItem value="AMBIGUOUS">Ambiguous</MenuItem>
          <MenuItem value="JOB_PENDING">Job Pending</MenuItem>
          <MenuItem value="MATCHED">Matched</MenuItem>
          <MenuItem value="IGNORED">Classified (non-customer)</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
      </PageFilters>

      {importMessage && (
        <Alert severity={importMessage.severity} sx={{ mb: 2 }} onClose={() => setImportMessage(null)}>
          {importMessage.text}
        </Alert>
      )}

      {statements.length > 0 && (
        <Box
          sx={{
            mb: 2,
            py: 1.5,
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Imported statements
          </Typography>
          {statements.map((statement) => (
            <Typography key={statement.id} variant="body2" color="text.secondary">
              {statement.bank} {statement.accountNumberMasked} ·{" "}
              {formatDate(statement.periodStart)}–{formatDate(statement.periodEnd)} ·
              closing {formatCurrency(statement.closingBalance)} · overhead{" "}
              {formatCurrency(statement.fixedOverhead)}
            </Typography>
          ))}
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>COA</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Extracted IDs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      No {statusFilter.toLowerCase()} transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>{formatDate(txn.occurredAt)}</TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {txn.direction === "debit" ? "−" : ""}
                        {formatCurrency(txn.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {txn.coaCode ? <Chip label={txn.coaCode} size="small" /> : "—"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {txn.referenceText || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {txn.extracted && (
                        <Box>
                          {txn.extracted.jobIds?.[0] && (
                            <Chip label={`Job: ${txn.extracted.jobIds[0]}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {txn.extracted.invoiceIds?.[0] && (
                            <Chip label={`Invoice: ${txn.extracted.invoiceIds[0]}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {txn.extracted.quoteIds?.[0] && (
                            <Chip label={`Quote: ${txn.extracted.quoteIds[0]}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {txn.extracted.referenceNumbers?.[0] && (
                            <Chip label={txn.extracted.referenceNumbers[0]} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={txn.status}
                        size="small"
                        color={
                          txn.status === "MATCHED"
                            ? "success"
                            : txn.status === "AMBIGUOUS"
                            ? "warning"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {txn.status !== "MATCHED" && (
                        <Button
                          size="small"
                          startIcon={<CheckCircle />}
                          onClick={() => handleMatchClick(txn)}
                        >
                          Match
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Match Transaction</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Amount: {formatCurrency(selectedTransaction.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Reference: {selectedTransaction.referenceText}
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <CustomerSearchAutocomplete
                  label="Search customer"
                  value={pickedCustomer}
                  initialInputValue={customerSearch}
                  onSearchTextChange={setCustomerSearch}
                  onChange={(customer) => {
                    setPickedCustomer(customer);
                    if (customer?.name) {
                      setCustomerSearch(customer.name);
                    }
                  }}
                />
              </Box>

              {searchError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {searchError}
                </Alert>
              )}

              {candidatesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              ) : candidates.length > 0 ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Suggested matches
                  </Typography>
                  {candidates.map((candidate) => {
                    const selected = isCandidateSelected(candidate, matchData);
                    return (
                      <Paper
                        key={`${candidate.invoiceId || ""}-${candidate.quoteId || ""}-${candidate.jobId || ""}`}
                        variant="outlined"
                        onClick={() => applyCandidate(candidate)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          cursor: "pointer",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: selected ? "action.selected" : "background.paper",
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" gap={1} alignItems="flex-start">
                          <Typography fontWeight={600}>
                            {candidate.customerName || "Unknown customer"}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${Math.round((candidate.confidence || 0) * 100)}%`}
                            color={candidate.confidence >= 0.75 ? "success" : "default"}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {candidate.reason}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Bank {formatCurrency(selectedTransaction.amount)} · Record{" "}
                          {formatCurrency(candidate.outstanding ?? candidate.amount)}
                          {candidate.paymentType ? ` · ${candidate.paymentType}` : ""}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                          {candidate.referenceNumber && (
                            <Chip label={candidate.referenceNumber} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {candidate.invoiceId && (
                            <Chip label={candidate.invoiceId} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {candidate.quoteId && (
                            <Chip label={candidate.quoteId} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {candidate.jobId && (
                            <Chip label={candidate.jobId} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {customerSearch.trim().length >= 2
                    ? `No customer, quote, or invoice is named “${customerSearch.trim()}”. The bank payer may not exist in this workspace — pick a known customer above, or enter IDs manually.`
                    : "No quote or invoice looked like this line. Search a customer name, or enter IDs manually."}
                </Alert>
              )}

              <Button
                size="small"
                onClick={() => setShowManualIds((open) => !open)}
                sx={{ mt: 1, textTransform: "none" }}
              >
                {showManualIds ? "Hide manual IDs" : "Enter IDs manually"}
              </Button>

              {showManualIds && (
                <Box>
                  <TextField
                    fullWidth
                    label="Invoice ID"
                    value={matchData.invoiceId}
                    onChange={(e) => setMatchData({ ...matchData, invoiceId: e.target.value })}
                    sx={{ mt: 2 }}
                    placeholder="INV-YYYYMMDD-XXX"
                  />
                  <TextField
                    fullWidth
                    label="Quote ID"
                    value={matchData.quoteId}
                    onChange={(e) => setMatchData({ ...matchData, quoteId: e.target.value })}
                    sx={{ mt: 2 }}
                    placeholder="QT-YYYYMMDD-XXX"
                  />
                  <TextField
                    fullWidth
                    label="Job ID"
                    value={matchData.jobId}
                    onChange={(e) => setMatchData({ ...matchData, jobId: e.target.value })}
                    sx={{ mt: 2 }}
                    placeholder="BUB-YYYYMMDD-XXX"
                  />
                </Box>
              )}

              <TextField
                fullWidth
                select
                label="Payment Type"
                value={matchData.type}
                onChange={(e) => setMatchData({ ...matchData, type: e.target.value })}
                sx={{ mt: 2 }}
              >
                <MenuItem value="DEPOSIT">Deposit</MenuItem>
                <MenuItem value="BALANCE">Balance</MenuItem>
                <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
              </TextField>

              {(matchMutation.isError || matchMutation.data?.success === false) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {matchMutation.data?.error ||
                    matchMutation.error?.message ||
                    "Could not match this transaction"}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMatchSubmit}
            variant="contained"
            disabled={matchMutation.isPending || (!matchData.invoiceId && !matchData.quoteId && !matchData.jobId)}
          >
            {matchMutation.isPending ? <CircularProgress size={20} /> : "Match"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ReconciliationQueueView;
